import { V2_ENDPOINT } from "@/consts";
import { authenticatedFetch } from "@/lib/authenticatedFetch";
import { useState, useCallback } from "react";
export interface TestActionParams {
  actionType: string;
  inputs: Record<string, unknown>;
  connector?: {
    id: string;
    type: string;
  };
}
export interface TestActionResponse {
  result?: Record<string, unknown>;
  error?: string;
}
export type TestStatus = "idle" | "loading" | "success" | "error";
export interface TestResult {
  status: TestStatus;
  result?: Record<string, unknown>;
  error?: string;
  duration?: number;
}

/**
 * Hook for testing actions via the /v1/actions/{action-type}/test endpoint
 */
export function useActionTest() {
  const [isLoading, setIsLoading] = useState(false);
  const [testResult, setTestResult] = useState<TestResult>({
    status: "idle"
  });
  const testAction = useCallback(async (params: TestActionParams): Promise<Record<string, unknown>> => {
    setIsLoading(true);
    setTestResult({
      status: "loading"
    });
    const startTime = Date.now();
    try {
      const response = await authenticatedFetch<TestActionResponse>(`${V2_ENDPOINT}/v1/actions/${params.actionType}/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inputs: params.inputs,
          ...(params.connector && {
            connector: params.connector
          })
        })
      });
      const duration_0 = Date.now() - startTime;
      if (!response.ok) {
        let errorMessage_0 = `Test failed with status ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage_0 = errorData.error || errorData.message || errorMessage_0;
        } catch {
          // Response wasn't JSON, use status error
        }
        setTestResult({
          status: "error",
          error: errorMessage_0,
          duration: duration_0
        });
        throw new Error(errorMessage_0);
      }
      const data = await response.json();
      if (data.error) {
        setTestResult({
          status: "error",
          error: data.error,
          duration: duration_0
        });
        throw new Error(data.error);
      }

      // Check for action-level failure (data.data.success === false)
      const actionData = data.data;
      if (actionData && actionData.success === false) {
        const errorMessage_1 = actionData.error?.message || "Action execution failed";
        setTestResult({
          status: "error",
          error: errorMessage_1,
          result: actionData,
          duration: duration_0
        });
        throw new Error(errorMessage_1);
      }
      const result = data.result || data;
      setTestResult({
        status: "success",
        result,
        duration: duration_0
      });
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : "Test failed";
      setTestResult(prev => prev.status === "error" ? prev : {
        status: "error",
        error: errorMessage,
        duration
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);
  const resetTest = useCallback(() => {
    setTestResult({
      status: "idle"
    });
  }, []);
  return {
    testAction,
    isLoading,
    testResult,
    resetTest
  };
}